import React, { ComponentType, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowUpRight, ChevronDown, ChevronUp, Filter, X } from 'lucide-react'
import { FilterPanel, FilterSection, FilterField, FilterFieldButton, FilterDropdown, FilterDropdownItem } from '../common/FilterPanel.tsx'
import DualRangeSlider from '../common/DualRangeSlider.tsx'
import styles from './FilterPublicProject.module.scss'

import { filterPublicProjects } from '../../../store/actions'
import { JURISDICTIONS_WITH_ALL } from '../../../constants/jurisdictions.ts'

type Option = {
  id: number
  name: string
}

type FilterPublicProjectProps = {
  filterPublicProjects: (filter: any, page: number, pageSize: number) => void
  setIsAdjudicated: (value: boolean) => void
  resultados: number
  totalDeProyectosMostrados: number
  currentPage: number
  initialProjectType?: 'A licitar' | 'Adjudicadas'
}

const DEFAULT_PAGE_SIZE = 9

const currencyOptions = ['Pesos', 'Dólares'] as const
const sortOptions = ['Recientes', 'Antiguos'] as const
const processOptions: Array<'A licitar' | 'Adjudicadas'> = ['A licitar', 'Adjudicadas']

const Presence = AnimatePresence as unknown as ComponentType<{ children: ReactNode; initial?: boolean }>

const FilterPublicProject = ({
  filterPublicProjects: dispatchFilter,
  setIsAdjudicated,
  resultados,
  totalDeProyectosMostrados,
  currentPage,
  initialProjectType = 'A licitar',
}: FilterPublicProjectProps) => {
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [mobileAdvancedOpen, setMobileAdvancedOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState<'A licitar' | 'Adjudicadas'>(initialProjectType)
  const [selectedOptionOrganism, setSelectedOptionOrganism] = useState<Option>({ id: 0, name: 'Todos' })
  const [selectedOptionJurisdiction, setSelectedOptionJurisdiction] = useState<Option>({ id: 0, name: 'Todas' })
  const [selectedWorkType, setSelectedWorkType] = useState<Option>({ id: 0, name: 'Todas' })
  const [selectedCurrency, setSelectedCurrency] = useState<typeof currencyOptions[number]>('Pesos')
  const [minAmount, setMinAmount] = useState(0)
  const [maxAmount, setMaxAmount] = useState(0)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedDateRange, setSelectedDateRange] = useState<string | null>(null)
  const [showExactDateInput, setShowExactDateInput] = useState(false)
  const [asociatedWords, setAsociatedWords] = useState('')
  const [selectedSort, setSelectedSort] = useState<typeof sortOptions[number]>('Recientes')
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false)

  const mobileFilterRef = useRef<HTMLDivElement | null>(null)

  const isAdjudicated = selectedOption === 'Adjudicadas'

  const filterCount = useMemo(() => {
    let count = 0
    if (selectedOption !== 'A licitar') count++
    if (selectedOptionOrganism.id !== 0) count++
    if (selectedOptionJurisdiction.id !== 0) count++
    if (selectedWorkType.id !== 0) count++
    if (selectedCurrency !== 'Pesos') count++
    if (minAmount > 0 || maxAmount > 0) count++
    if (selectedDate || selectedDateRange) count++
    if (asociatedWords.trim() !== '') count++
    if (selectedSort !== 'Recientes') count++
    return count
  }, [
    asociatedWords,
    maxAmount,
    minAmount,
    selectedCurrency,
    selectedDate,
    selectedDateRange,
    selectedOption,
    selectedOptionJurisdiction.id,
    selectedOptionOrganism.id,
    selectedSort,
    selectedWorkType.id,
  ])

  const formatDate = (value: string) => {
    const date = new Date(value)
    const day = String(date.getUTCDate()).padStart(2, '0')
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const year = date.getUTCFullYear()
    return `${day}/${month}/${year}`
  }

  const handleFilterClick = (filterName: string) => {
    setSelectedFilter((prev) => (prev === filterName ? null : filterName))
  }

  const handleProcessSelect = (option: 'A licitar' | 'Adjudicadas') => {
    setSelectedOption(option)
    setIsAdjudicated(option === 'Adjudicadas')
    setSelectedFilter(null)
  }

  const handleOrganismSelect = (option: Option) => {
    setSelectedOptionOrganism(option)
    setSelectedFilter(null)
  }

  const handleJurisdictionSelect = (option: Option) => {
    setSelectedOptionJurisdiction(option)
    setSelectedFilter(null)
  }

  const handleWorkTypeSelect = (option: Option) => {
    setSelectedWorkType(option)
    setSelectedFilter(null)
  }

  const generateDateRange = useCallback((label: string | null) => {
    if (!label) return null
    const today = new Date()
    const move = (days: number) => {
      const result = new Date(today)
      result.setDate(today.getDate() + days)
      return result
    }

    switch (label) {
      case 'Próximos 30 días':
        return [today, move(30)]
      case '30 a 60 días':
        return [move(30), move(60)]
      case '60 a 90 días':
        return [move(60), move(90)]
      default:
        return null
    }
  }, [])

  const currencyFormatter = useMemo(() => {
    const currency = selectedCurrency === 'Dólares' ? 'USD' : 'ARS'
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency, minimumFractionDigits: 0 })
  }, [selectedCurrency])

  const amountLimit = selectedCurrency === 'Pesos' ? 500_000_000 : 50_000_000

  const renderDropdown = useCallback(
    (items: Option[], onSelect: (option: Option) => void, activeId: number) => (
      <FilterDropdown>
        {items.map((item) => (
          <FilterDropdownItem
            key={item.id}
            onSelect={() => {
              onSelect(item)
              setSelectedFilter(null)
            }}
            active={item.id === activeId}
          >
            {item.name}
          </FilterDropdownItem>
        ))}
      </FilterDropdown>
    ),
    [],
  )

  const buildFilterPayload = useCallback(() => {
    const toISO = (value: Date) => {
      const year = value.getFullYear()
      const month = String(value.getMonth() + 1).padStart(2, '0')
      const day = String(value.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    const dateRange = generateDateRange(selectedDateRange)
    const dateFrom = selectedDate ? toISO(new Date()) : dateRange ? toISO(dateRange[0]) : null
    const dateTo = selectedDate ? selectedDate : dateRange ? toISO(dateRange[1]) : null

    return {
      sort: selectedSort === 'Recientes' ? 'DESC' : 'ASC',
      asociatedWords,
      zone: null,
      minAmount: minAmount > 0 ? minAmount : null,
      maxAmount: maxAmount > 0 ? maxAmount : null,
      currency: selectedCurrency === 'Dólares' ? 'USD' : 'ARS',
      jurisdiction: selectedOptionJurisdiction.id !== 0 ? selectedOptionJurisdiction.id : null,
      organism: selectedOptionOrganism.id !== 0 ? selectedOptionOrganism.id : null,
      workType: selectedWorkType.id !== 0 ? selectedWorkType.id : null,
      dateRange: { dateFrom, dateTo },
      projectType: selectedOption,
    }
  }, [
    asociatedWords,
    maxAmount,
    minAmount,
    selectedCurrency,
    selectedDate,
    selectedDateRange,
    selectedOption,
    selectedOptionJurisdiction.id,
    selectedOptionOrganism.id,
    selectedSort,
    selectedWorkType.id,
    generateDateRange,
  ])

  const handleFilter = useCallback(() => {
    const payload = buildFilterPayload()
    dispatchFilter(payload, currentPage, DEFAULT_PAGE_SIZE)
  }, [buildFilterPayload, currentPage, dispatchFilter])

  const handleReset = () => {
    setSelectedOption(initialProjectType)
    setIsAdjudicated(initialProjectType === 'Adjudicadas')
    setSelectedOptionOrganism({ id: 0, name: 'Todos' })
    setSelectedOptionJurisdiction({ id: 0, name: 'Todas' })
    setSelectedWorkType({ id: 0, name: 'Todas' })
    setSelectedCurrency('Pesos')
    setMinAmount(0)
    setMaxAmount(0)
    setSelectedDate(null)
    setSelectedDateRange(null)
    setShowExactDateInput(false)
    setAsociatedWords('')
    setSelectedSort('Recientes')
    setSelectedFilter(null)
    setAdvancedOpen(false)
    setMobileAdvancedOpen(false)
  }

  const summary = (
    <div className={styles.summaryRow}>
      <p className={styles.summaryText}>
        {resultados === 0 ? (
          'No se encontraron resultados'
        ) : resultados === 1 ? (
          <>
            Se encontró <span className={styles.summaryHighlight}>1</span> resultado
          </>
        ) : (
          <>
            Se encontraron <span className={styles.summaryHighlight}>{resultados}</span> resultados
          </>
        )}
        {resultados > DEFAULT_PAGE_SIZE && (
          <span className={styles.summaryExtra}>
            · Mostrando {totalDeProyectosMostrados} de {resultados}
          </span>
        )}
      </p>
      <div className={styles.sortControl}>
        <span className={styles.sortLabel}>Ordenar por</span>
        <button
          type="button"
          className={styles.sortButton}
          onClick={() => setSelectedSort((prev) => (prev === 'Recientes' ? 'Antiguos' : 'Recientes'))}
        >
          {selectedSort}
          <ChevronDown size={16} />
        </button>
      </div>
    </div>
  )

  const primary = (
    <>
      <FilterSection>
        <div className={styles.toggleGroup}>
          {processOptions.map((option) => (
            <button
              key={option}
              type="button"
              className={styles.toggle}
              data-active={option === selectedOption}
              onClick={() => handleProcessSelect(option)}
            >
              {option}
            </button>
          ))}
        </div>
      </FilterSection>

      <FilterSection>
        <FilterField>
          <FilterFieldButton
            title="Organismo"
            value={selectedOptionOrganism.name}
            onClick={() => handleFilterClick('organism')}
            active={selectedFilter === 'organism'}
          />
          {selectedFilter === 'organism' &&
            renderDropdown(ORGANISM_OPTIONS, handleOrganismSelect, selectedOptionOrganism.id)}
        </FilterField>
      </FilterSection>

      <FilterSection>
        <FilterField>
          <FilterFieldButton
            title="Jurisdicción"
            value={selectedOptionJurisdiction.name}
            onClick={() => handleFilterClick('jurisdiction')}
            active={selectedFilter === 'jurisdiction'}
          />
          {selectedFilter === 'jurisdiction' &&
            renderDropdown(JURISDICTION_OPTIONS, handleJurisdictionSelect, selectedOptionJurisdiction.id)}
        </FilterField>
      </FilterSection>

      <FilterSection>
        <FilterField>
          <FilterFieldButton
            title="Tipo de obra"
            value={selectedWorkType.name}
            onClick={() => handleFilterClick('workType')}
            active={selectedFilter === 'workType'}
          />
          {selectedFilter === 'workType' &&
            renderDropdown(PUBLIC_WORK_TYPES, handleWorkTypeSelect, selectedWorkType.id)}
        </FilterField>
      </FilterSection>
    </>
  )

  const advanced = (
    <>
      <FilterSection>
        <div className={styles.pillGroup}>
          {currencyOptions.map((option) => (
            <button
              key={option}
              type="button"
              className={styles.pill}
              data-active={option === selectedCurrency}
              onClick={() => setSelectedCurrency(option)}
            >
              {option}
            </button>
          ))}
        </div>
      </FilterSection>

      <FilterSection>
        <div className={styles.rangeControl}>
          <DualRangeSlider
            min={0}
            max={amountLimit}
            step={selectedCurrency === 'Pesos' ? 1_000_000 : 1_000}
            values={[minAmount, maxAmount === 0 ? amountLimit : maxAmount]}
            onChange={([nextMin, nextMax]) => {
              setMinAmount(nextMin)
              setMaxAmount(nextMax === amountLimit ? 0 : nextMax)
            }}
            formatLabels={([currentMin, currentMax]) => {
              const minLabel = currentMin === 0 ? 'Sin mínimo' : currencyFormatter.format(currentMin)
              const maxLabel = maxAmount === 0 ? 'Sin máximo' : currencyFormatter.format(currentMax)
              return [minLabel, maxLabel]
            }}
            presets={
              selectedCurrency === 'Pesos'
                ? [
                    { label: 'Hasta 50M', values: [0, 50_000_000] },
                    { label: '50M a 150M', values: [50_000_000, 150_000_000] },
                    { label: '150M a 300M', values: [150_000_000, 300_000_000] },
                    { label: '300M+', values: [300_000_000, amountLimit] },
                  ]
                : [
                    { label: 'Hasta 5M', values: [0, 5_000_000] },
                    { label: '5M a 15M', values: [5_000_000, 15_000_000] },
                    { label: '15M a 30M', values: [15_000_000, 30_000_000] },
                    { label: '30M+', values: [30_000_000, amountLimit] },
                  ]
            }
          />
        </div>
      </FilterSection>

      <FilterSection>
        <FilterField>
          <FilterFieldButton
            title="Rango"
            value={selectedDateRange || (selectedDate ? formatDate(selectedDate) : undefined)}
            placeholder="Elegir"
            onClick={() => {
              setAdvancedOpen(true)
              handleFilterClick('date')
            }}
            active={selectedFilter === 'date'}
          />
          {selectedFilter === 'date' && (
            <FilterDropdown>
              {['Próximos 30 días', '30 a 60 días', '60 a 90 días'].map((label) => (
                <FilterDropdownItem
                  key={label}
                  onSelect={() => {
                    setSelectedDateRange(label)
                    setSelectedDate(null)
                    setShowExactDateInput(false)
                    setSelectedFilter(null)
                  }}
                  active={selectedDateRange === label}
                >
                  {label}
                </FilterDropdownItem>
              ))}
              <div className={styles.dropdownFooter}>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => {
                    setSelectedDateRange(null)
                    setShowExactDateInput(true)
                  }}
                >
                  Fecha exacta
                </button>
                {showExactDateInput && (
                  <input
                    type="date"
                    className={styles.dateInput}
                    value={selectedDate ?? ''}
                    onChange={(event) => setSelectedDate(event.target.value)}
                  />
                )}
              </div>
            </FilterDropdown>
          )}
        </FilterField>
      </FilterSection>

      <FilterSection>
        <input
          type="text"
          className={styles.textInput}
          value={asociatedWords}
          placeholder="Ej. Vivienda, pavimentación..."
          onChange={(event) => setAsociatedWords(event.target.value)}
        />
      </FilterSection>
    </>
  )

  const renderDesktop = () => (
    <FilterPanel
      title="Proyectos públicos"
      filterCount={filterCount}
      onReset={handleReset}
      onToggleAdvanced={() => {
        setAdvancedOpen((prev) => !prev)
        setSelectedFilter(null)
      }}
      advancedOpen={advancedOpen}
      summary={summary}
      primary={primary}
      advanced={advanced}
    />
  )

  const renderMobile = () => (
    <>
      <div className={styles.mobileStickyControls}>
        <button type="button" className={styles.mobileTrigger} onClick={handleMobileOpen}>
          <Filter size={18} />
          Filtros
          {filterCount > 0 && <span className={styles.mobileBadge}>{filterCount}</span>}
        </button>
        <button
          type="button"
          className={styles.mobileSortButton}
          onClick={() => setSelectedSort((prev) => (prev === 'Recientes' ? 'Antiguos' : 'Recientes'))}
        >
          {selectedSort}
          <ChevronDown size={16} />
        </button>
      </div>

      <Presence initial={false}>
        {mobileFilterOpen && (
          <motion.div
            className={styles.mobileOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              ref={mobileFilterRef}
              className={styles.mobileSheet}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
            >
              <header className={styles.mobileHeader}>
                <div>
                  <h3>Filtros</h3>
                  <p>{resultados} resultados</p>
                </div>
                <button type="button" className={styles.mobileClose} onClick={handleMobileClose}>
                  <X size={20} />
                </button>
              </header>

              <div className={styles.mobileContent}>
                <div className={styles.mobileSegmented}>
                  {processOptions.map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={styles.segmented}
                      data-active={option === selectedOption}
                      onClick={() => handleProcessSelect(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>

                <FilterFieldButton
                  title="Organismo"
                  value={selectedOptionOrganism.name}
                  onClick={() => handleFilterClick('organism-mobile')}
                  active={selectedFilter === 'organism-mobile'}
                />
                <Presence initial={false}>
                  {selectedFilter === 'organism-mobile' && (
                    <motion.div
                      key="organism-mobile-dropdown"
                      className={styles.mobileDropdown}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{
                        height: 'auto',
                        opacity: 1,
                        transition: { duration: 0.24, ease: 'easeOut' },
                        transitionEnd: { overflowY: 'auto' },
                      }}
                      exit={{
                        height: 0,
                        opacity: 0,
                        transition: { duration: 0.18, ease: 'easeIn' },
                        transitionEnd: { overflowY: 'hidden' },
                      }}
                      style={{ overflowY: 'hidden' }}
                    >
                      {ORGANISM_OPTIONS.map((option) => (
                        <FilterDropdownItem
                          key={option.id}
                          onSelect={() => {
                            handleOrganismSelect(option)
                            setSelectedFilter(null)
                          }}
                          active={selectedOptionOrganism.id === option.id}
                        >
                          {option.name}
                        </FilterDropdownItem>
                      ))}
                    </motion.div>
                  )}
                </Presence>

                <FilterFieldButton
                  title="Jurisdicción"
                  value={selectedOptionJurisdiction.name}
                  onClick={() => handleFilterClick('jurisdiction-mobile')}
                  active={selectedFilter === 'jurisdiction-mobile'}
                />
                <Presence initial={false}>
                  {selectedFilter === 'jurisdiction-mobile' && (
                    <motion.div
                      key="jurisdiction-mobile-dropdown"
                      className={styles.mobileDropdown}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{
                        height: 'auto',
                        opacity: 1,
                        transition: { duration: 0.24, ease: 'easeOut' },
                        transitionEnd: { overflowY: 'auto' },
                      }}
                      exit={{
                        height: 0,
                        opacity: 0,
                        transition: { duration: 0.18, ease: 'easeIn' },
                        transitionEnd: { overflowY: 'hidden' },
                      }}
                      style={{ overflowY: 'hidden' }}
                    >
                      {JURISDICTION_OPTIONS.map((option) => (
                        <FilterDropdownItem
                          key={option.id}
                          onSelect={() => {
                            handleJurisdictionSelect(option)
                            setSelectedFilter(null)
                          }}
                          active={selectedOptionJurisdiction.id === option.id}
                        >
                          {option.name}
                        </FilterDropdownItem>
                      ))}
                    </motion.div>
                  )}
                </Presence>

                <FilterFieldButton
                  title="Tipo de obra"
                  value={selectedWorkType.name}
                  onClick={() => handleFilterClick('workType-mobile')}
                  active={selectedFilter === 'workType-mobile'}
                />
                <Presence initial={false}>
                  {selectedFilter === 'workType-mobile' && (
                    <motion.div
                      key="worktype-mobile-dropdown"
                      className={styles.mobileDropdown}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{
                        height: 'auto',
                        opacity: 1,
                        transition: { duration: 0.24, ease: 'easeOut' },
                        transitionEnd: { overflowY: 'auto' },
                      }}
                      exit={{
                        height: 0,
                        opacity: 0,
                        transition: { duration: 0.18, ease: 'easeIn' },
                        transitionEnd: { overflowY: 'hidden' },
                      }}
                      style={{ overflowY: 'hidden' }}
                    >
                      {PUBLIC_WORK_TYPES.map((option) => (
                        <FilterDropdownItem
                          key={option.id}
                          onSelect={() => {
                            handleWorkTypeSelect(option)
                            setSelectedFilter(null)
                          }}
                          active={selectedWorkType.id === option.id}
                        >
                          {option.name}
                        </FilterDropdownItem>
                      ))}
                    </motion.div>
                  )}
                </Presence>

                <button
                  type="button"
                  className={styles.mobileAdvancedToggle}
                  onClick={() => setMobileAdvancedOpen((open) => !open)}
                >
                  Búsqueda avanzada
                  {mobileAdvancedOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>

                <Presence initial={false}>
                  {mobileAdvancedOpen && (
                    <motion.div
                      className={styles.mobileAdvanced}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <div className={styles.mobilePills}>
                        {currencyOptions.map((option) => (
                          <button
                            key={option}
                            type="button"
                            className={option === selectedCurrency ? styles.pillActive : styles.pill}
                            onClick={() => setSelectedCurrency(option)}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                      <div className={styles.mobileRangeControl}>
                        <DualRangeSlider
                          min={0}
                          max={amountLimit}
                          step={selectedCurrency === 'Pesos' ? 1_000_000 : 1_000}
                          values={[minAmount, maxAmount === 0 ? amountLimit : maxAmount]}
                          onChange={([nextMin, nextMax]) => {
                            setMinAmount(nextMin)
                            setMaxAmount(nextMax === amountLimit ? 0 : nextMax)
                          }}
                          formatLabels={([currentMin, currentMax]) => {
                            const minLabel = currentMin === 0 ? 'Sin mínimo' : currencyFormatter.format(currentMin)
                            const maxLabel = maxAmount === 0 ? 'Sin máximo' : currencyFormatter.format(currentMax)
                            return [minLabel, maxLabel]
                          }}
                          presets={[]}
                        />
                      </div>

                      <FilterFieldButton
                        title={isAdjudicated ? 'Fecha de adjudicación' : 'Fecha de apertura'}
                        value={selectedDateRange || (selectedDate ? formatDate(selectedDate) : undefined)}
                        placeholder="Elegir"
                        onClick={() => handleFilterClick('date-mobile')}
                        active={selectedFilter === 'date-mobile'}
                      />
                      <Presence initial={false}>
                        {selectedFilter === 'date-mobile' && (
                          <motion.div
                            key="date-mobile-dropdown"
                            className={styles.mobileDropdown}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{
                              height: 'auto',
                              opacity: 1,
                              transition: { duration: 0.24, ease: 'easeOut' },
                              transitionEnd: { overflowY: 'auto' },
                            }}
                            exit={{
                              height: 0,
                              opacity: 0,
                              transition: { duration: 0.18, ease: 'easeIn' },
                              transitionEnd: { overflowY: 'hidden' },
                            }}
                            style={{ overflowY: 'hidden' }}
                          >
                            {['Próximos 30 días', '30 a 60 días', '60 a 90 días'].map((label) => (
                              <FilterDropdownItem
                                key={label}
                                onSelect={() => {
                                  setSelectedDateRange(label)
                                  setSelectedDate(null)
                                  setShowExactDateInput(false)
                                  setSelectedFilter(null)
                                }}
                                active={selectedDateRange === label}
                              >
                                {label}
                              </FilterDropdownItem>
                            ))}
                            <div className={styles.dropdownFooter}>
                              <button
                                type="button"
                                className={styles.linkButton}
                                onClick={() => {
                                  setSelectedDateRange(null)
                                  setShowExactDateInput(true)
                                }}
                              >
                                Fecha exacta
                              </button>
                              {showExactDateInput && (
                                <input
                                  type="date"
                                  className={styles.dateInput}
                                  value={selectedDate ?? ''}
                                  onChange={(event) => setSelectedDate(event.target.value)}
                                />
                              )}
                            </div>
                          </motion.div>
                        )}
                      </Presence>

                      <input
                        type="text"
                        className={styles.textInput}
                        value={asociatedWords}
                        placeholder="Palabras clave"
                        onChange={(event) => setAsociatedWords(event.target.value)}
                      />
                    </motion.div>
                  )}
                </Presence>
              </div>

              <footer className={styles.mobileFooter}>
                <button type="button" className={styles.mobileReset} onClick={handleReset}>
                  Limpiar filtros
                </button>
                <button type="button" className={styles.mobileApply} onClick={handleMobileClose}>
                  Aplicar
                  <ArrowUpRight size={18} />
                </button>
              </footer>
            </motion.div>
          </motion.div>
        )}
      </Presence>
    </>
  )

  const handleMobileOpen = () => {
    setMobileFilterOpen(true)
    document.body.style.overflow = 'hidden'
  }

  const handleMobileClose = () => {
    setMobileFilterOpen(false)
    setMobileAdvancedOpen(false)
    document.body.style.overflow = ''
  }

  useEffect(() => {
    const listener = (event: MouseEvent) => {
      if (mobileFilterRef.current && !mobileFilterRef.current.contains(event.target as Node)) {
        handleMobileClose()
      }
    }

    if (mobileFilterOpen) {
      document.addEventListener('mousedown', listener)
    }

    return () => {
      document.removeEventListener('mousedown', listener)
    }
  }, [mobileFilterOpen])

  useEffect(() => {
    handleFilter()
  }, [handleFilter])

  return (
    <div className={styles.root}>
      <div className={styles.desktop}>{renderDesktop()}</div>
      <div className={styles.mobile}>{renderMobile()}</div>
    </div>
  )
}

const mapStateToProps = (state: any) => ({
  resultados: state.dashboard.itemCount,
  totalDeProyectosMostrados: state.dashboard.allProjects?.length ?? 0,
})

const mapDispatchToProps = {
  filterPublicProjects,
}

const JURISDICTION_OPTIONS: Option[] = JURISDICTIONS_WITH_ALL as Option[]

const ORGANISM_OPTIONS: Option[] = [
  { id: 0, name: 'Todos' },
  { id: 1, name: 'Nacional' },
  { id: 2, name: 'Provincial' },
  { id: 3, name: 'Municipal' },
  { id: 4, name: 'Empresa Pública' },
]

const PUBLIC_WORK_TYPES: Option[] = [
  { id: 1, name: 'Obras Viales' },
  { id: 2, name: 'Obras eléctricas' },
  { id: 3, name: 'Obras de gas' },
  { id: 4, name: 'Obras Sanitarias / Saneamiento' },
  { id: 5, name: 'Obras de Arquitectura (Nuevas)' },
  { id: 6, name: 'Obras de Arquitectura (Remodelaciones)' },
  { id: 7, name: 'Servicios profesionales' },
  { id: 8, name: 'Servicios de consultoría' },
  { id: 9, name: 'Servicios varios' },
  { id: 10, name: 'Obras menores y Pequeñas' },
  { id: 11, name: 'Obras Hidráulicas' },
  { id: 12, name: 'Materiales de Construcción' },
  { id: 13, name: 'Maquinarias y Herramientas' },
  { id: 14, name: 'Otras Obras' },
]

export default connect(mapStateToProps, mapDispatchToProps)(FilterPublicProject)
